import { Project } from '@/stores/ProjectStore';
import { HelpOutline } from '@mui/icons-material';
import {
  Box,
  FormControl,
  FormLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { LineItemState, parseAmount } from './types';

const FONT_HEADER = 'Montserrat, sans-serif';

// Mirrors REFERENCE_YEAR_MIN / REFERENCE_YEAR_MAX in
// server/csisite/api/cost_estimates/models.py, which is the source of truth
// (the API rejects years outside it). Fixed on purpose: offering a year implies
// reference data exists for it, so the range must not slide with the calendar.
const REFERENCE_YEAR_MIN = 2020;
const REFERENCE_YEAR_MAX = 2024;
const REFERENCE_YEARS = Array.from(
  { length: REFERENCE_YEAR_MAX - REFERENCE_YEAR_MIN + 1 },
  (_, i) => (REFERENCE_YEAR_MIN + i).toString(),
);

// Shared sx for form controls on light (#D5E4D5) background
const lightFieldSx = {
  '& .MuiInputLabel-root': { color: 'rgba(14, 70, 63, 0.7)' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#0E463F' },
  '& .MuiOutlinedInput-root': {
    color: 'primary.main',
    '& fieldset': { borderColor: 'rgba(14, 70, 63, 0.3)' },
    '&:hover fieldset': { borderColor: 'rgba(14, 70, 63, 0.5)' },
    '&.Mui-focused fieldset': { borderColor: '#0E463F', borderWidth: 2 },
  },
  '& .MuiSelect-icon': { color: 'primary.main' },
};

// Same palette as lightFieldSx, applied to the exclusive toggle used for the
// cost basis (ToggleButtonGroup has no outlined-input slots to target).
const lightToggleSx = {
  '& .MuiFormLabel-root': {
    color: 'rgba(14, 70, 63, 0.7)',
    fontSize: '0.75rem',
    mb: 0.5,
  },
  '& .MuiFormLabel-root.Mui-focused': { color: '#0E463F' },
  '& .MuiToggleButton-root': {
    color: 'rgba(14, 70, 63, 0.7)',
    borderColor: 'rgba(14, 70, 63, 0.3)',
    textTransform: 'none',
    '&:hover': { borderColor: 'rgba(14, 70, 63, 0.5)' },
    '&.Mui-selected, &.Mui-selected:hover': {
      color: 'white',
      bgcolor: '#0E463F',
      borderColor: '#0E463F',
    },
  },
};

function computeTotal(
  items: LineItemState[],
  field: keyof Pick<
    LineItemState,
    | 'ownerAmountFiscalyear'
    | 'contractorAmountFiscalyear'
    | 'ownerAmountTotal'
    | 'contractorAmountTotal'
  >,
): number | null {
  const hasAny = items.some((item) => parseAmount(item[field]) !== null);
  if (!hasAny) return null;
  return items.reduce((sum, item) => sum + (parseAmount(item[field]) ?? 0), 0);
}

function formatTotal(n: number | null): string {
  if (n === null) return '—';
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function SubSectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        px: 1.5,
        py: 0.75,
        bgcolor: 'background.paper',
      }}
    >
      <Typography
        variant='caption'
        sx={{
          fontFamily: FONT_HEADER,
          fontWeight: 700,
          color: 'text.primary',
          textTransform: 'uppercase',
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}

// System-derived multiplier from the API as a decimal string ('1.2100').
// null means the project has no single regional multiplier (its subprojects
// resolve to different ones, or it has none) -- shown as an em dash.
function formatMultiplier(v: string | null | undefined): string {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  return Number.isNaN(n) ? '—' : `×${n}`;
}

function SummaryRow({
  label,
  value,
  bgcolor,
  pl,
  pr,
}: {
  label: string;
  value: string;
  bgcolor?: string;
  pl?: number;
  pr?: number;
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        pl: pl ?? 1.5,
        pr: pr ?? 1.5,
        py: 0.4,
        borderBottom: '1px solid',
        borderColor: 'rgba(14, 70, 63, 0.2)',
        ...(bgcolor && { bgcolor }),
      }}
    >
      <Typography
        variant='body2'
        sx={{ color: 'rgba(14, 70, 63, 0.7)', fontSize: '0.75rem' }}
      >
        {label}
      </Typography>
      <Typography
        variant='body2'
        sx={{ fontSize: '0.75rem', color: 'primary.main' }}
      >
        {value}
      </Typography>
    </Box>
  );
}

export interface CostSummarySidebarProps {
  capitalItems: LineItemState[];
  operationItems: LineItemState[];
  project?: Project | null;
  dataSource: string;
  referenceYear: string;
  rateInflationOwner: string;
  rateInflationLand: string;
  rateInflationCostEscalation: string;
  /** The USER's local cost multiplier (editable). */
  localCostMultiplier: string;
  /**
   * The SYSTEM's regional multiplier, derived from the project's shapes.
   * Always read-only; null when there is no single value.
   */
  regionalMultiplier: string | null;
  /** 'local' | 'national' | '' (not stated -> NULL on save) */
  costBasis: string;
  readOnly?: boolean;
  onDataSourceChange?: (v: string) => void;
  onReferenceYearChange?: (v: string) => void;
  onLocalCostMultiplierChange?: (v: string) => void;
  onCostBasisChange?: (v: string) => void;
}

// Mirrors DATA_SOURCE_CHOICES in server/csisite/api/cost_estimates/models.py
// (source of truth). Keys are the stored values -- join keys for the follow-on
// calibration check -- and must never change; labels are display-only.
const DATA_SOURCE_LABELS: Record<string, string> = {
  rs_means: 'RS Means',
  enr: 'ENR',
};

const COST_BASIS_LABELS: Record<string, string> = {
  local: 'Local',
  national: 'National',
};

const CALIBRATION_NOT_YET_APPLIED_NOTE =
  'Data Source, Reference Year, Local Cost Multiplier and Cost Basis are ' +
  'saved with your project but do not yet change the cost estimates.';

export function CostSummarySidebar({
  capitalItems,
  operationItems,
  project,
  dataSource,
  referenceYear,
  rateInflationOwner: _rateInflationOwner,
  rateInflationLand: _rateInflationLand,
  rateInflationCostEscalation: _rateInflationCostEscalation,
  localCostMultiplier,
  regionalMultiplier,
  costBasis,
  readOnly = false,
  onDataSourceChange,
  onReferenceYearChange,
  onLocalCostMultiplierChange,
  onCostBasisChange,
}: CostSummarySidebarProps) {
  const [tooltipAnchor, setTooltipAnchor] = useState<HTMLElement | null>(null);
  const tooltipOpen = Boolean(tooltipAnchor);

  const fyName = project?.fiscalYearDetails?.name ?? 'FY??';

  // Computed totals — FY
  const capitalFyOwner = computeTotal(capitalItems, 'ownerAmountFiscalyear');
  const capitalFyContractor = computeTotal(
    capitalItems,
    'contractorAmountFiscalyear',
  );
  const capitalFyTotal =
    capitalFyOwner !== null || capitalFyContractor !== null
      ? (capitalFyOwner ?? 0) + (capitalFyContractor ?? 0)
      : null;

  // Computed totals — Total Project
  const capitalTotalOwner = computeTotal(capitalItems, 'ownerAmountTotal');
  const capitalTotalContractor = computeTotal(
    capitalItems,
    'contractorAmountTotal',
  );
  const capitalTotalTotal =
    capitalTotalOwner !== null || capitalTotalContractor !== null
      ? (capitalTotalOwner ?? 0) + (capitalTotalContractor ?? 0)
      : null;

  const operationOwner = computeTotal(operationItems, 'ownerAmountFiscalyear');
  const operationContractor = computeTotal(
    operationItems,
    'contractorAmountFiscalyear',
  );
  const operationTotal =
    operationOwner !== null || operationContractor !== null
      ? (operationOwner ?? 0) + (operationContractor ?? 0)
      : null;

  const grandTotal =
    capitalFyTotal !== null ||
    capitalTotalTotal !== null ||
    operationTotal !== null
      ? (capitalFyTotal ?? 0) + (capitalTotalTotal ?? 0) + (operationTotal ?? 0)
      : null;

  return (
    <Stack spacing={2}>
      {/* What is this? — hidden in readonly mode */}
      {!readOnly && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            justifyContent: 'flex-end',
            backgroundColor: 'background.paper',
            borderRadius: '4px 4px 0 0',
          }}
        >
          <IconButton
            size='small'
            onClick={(e) => setTooltipAnchor(e.currentTarget)}
            aria-label='What is this?'
          >
            <HelpOutline fontSize='small' sx={{ color: 'white' }} />
          </IconButton>
          <Typography
            variant={'caption'}
            sx={{ cursor: 'pointer', color: 'white', paddingRight: '10px' }}
            onClick={(e) => setTooltipAnchor(e.currentTarget)}
          >
            What is this?
          </Typography>
          <Popover
            open={tooltipOpen}
            anchorEl={tooltipAnchor}
            onClose={() => setTooltipAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          >
            <Typography sx={{ p: 2, maxWidth: 280, fontSize: '0.85rem' }}>
              Select a familiar source of cost estimates, its reference year and
              whether its figures are local or national, then enter your Local
              Cost Multiplier (how your local costs compare to that source).
              These inputs are saved with your project but do not yet change the
              cost estimates; they will later be used to check how accurate our
              estimates are for you. The Regional Multiplier is calculated from
              your project&apos;s site location, is already reflected in our
              estimates, and cannot be edited.
            </Typography>
          </Popover>
        </Box>
      )}

      {/*
        Calibration controls (source, year, multiplier, and the local/national
        basis once it exists) live HERE and nowhere else. Decided in
        decarb4-user-calibration/002, option (a): they stay in the sidebar
        rather than moving into the "Capital Cost Estimation" section, because
        the sidebar spans capital AND annual costs and calibration will extend
        to maintenance. Do not add a second editor of CostEstimate: the PUT
        replaces line items wholesale, so a second writer is destructive.
      */}
      {/* Data Source — editable or readonly */}
      <Box>
        {readOnly ? (
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'rgba(14, 70, 63, 0.2)',
              borderRadius: '4px 4px 0 0',
              overflow: 'hidden',
            }}
          >
            <SummaryRow
              label='Data Source'
              value={(DATA_SOURCE_LABELS[dataSource] ?? dataSource) || '—'}
              bgcolor='#D5E4D5'
            />
            <SummaryRow
              label='Reference Year'
              value={referenceYear || '—'}
              bgcolor='#CCDCCC'
            />
            <SummaryRow
              label='Regional Multiplier'
              value={formatMultiplier(regionalMultiplier)}
              bgcolor='#D5E4D5'
            />
            <SummaryRow
              label='Local Cost Multiplier'
              value={localCostMultiplier ? `×${localCostMultiplier}` : '—'}
              bgcolor='#CCDCCC'
            />
            <SummaryRow
              label='Cost Basis'
              value={(COST_BASIS_LABELS[costBasis] ?? costBasis) || '—'}
              bgcolor='#D5E4D5'
            />
          </Box>
        ) : (
          <Stack
            spacing={3.5}
            sx={{
              pt: 0,
              px: 1.5,
              pb: 1.5,
              border: '1px solid',
              borderColor: 'rgba(14, 70, 63, 0.2)',
              borderRadius: '4px 4px 0 0',
              bgcolor: '#D5E4D5',
            }}
          >
            <FormControl size='small' fullWidth sx={lightFieldSx}>
              <InputLabel>Data Source</InputLabel>
              <Select
                value={dataSource}
                label='Data Source'
                onChange={(e) => onDataSourceChange!(e.target.value)}
              >
                {Object.entries(DATA_SOURCE_LABELS).map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size='small' fullWidth sx={lightFieldSx}>
              <InputLabel>Reference Year</InputLabel>
              <Select
                value={referenceYear}
                label='Reference Year'
                onChange={(e) => onReferenceYearChange!(e.target.value)}
              >
                {REFERENCE_YEARS.map((yr) => (
                  <MenuItem key={yr} value={yr}>
                    {yr}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/*
              Two different numbers, deliberately separate: the Regional
              Multiplier is the SYSTEM's (derived from the project's shapes,
              read-only); the Local Cost Multiplier is the USER's. The
              calibration check compares them, so they must never be one field.
            */}
            <SummaryRow
              label='Regional Multiplier'
              value={formatMultiplier(regionalMultiplier)}
              pl={0}
              pr={0}
            />

            <TextField
              size='small'
              label='Local Cost Multiplier'
              value={localCostMultiplier}
              onChange={(e) => onLocalCostMultiplierChange!(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position='start' sx={{ mr: 0 }}>
                      x
                    </InputAdornment>
                  ),
                },
              }}
              placeholder='1.0'
              sx={{
                ...lightFieldSx,
                '& input': { color: 'primary.main' },
                '& .MuiInputAdornment-root': { color: 'primary.main' },
                '& .MuiOutlinedInput-root input': { paddingLeft: '2px' },
              }}
            />

            {/*
              Local vs national is an operator, not a label: a national figure
              must be regionalized before the calibration check compares it.
              Clicking the selected option deselects it -> '' -> NULL
              ("not stated"), which the check treats as "do not run".
            */}
            <FormControl size='small' fullWidth sx={lightToggleSx}>
              <FormLabel id='cost-basis-label'>Cost Basis</FormLabel>
              <ToggleButtonGroup
                exclusive
                fullWidth
                size='small'
                value={costBasis || null}
                aria-labelledby='cost-basis-label'
                onChange={(_e, v: string | null) => onCostBasisChange!(v ?? '')}
              >
                <ToggleButton value='local'>Local</ToggleButton>
                <ToggleButton value='national'>National</ToggleButton>
              </ToggleButtonGroup>
            </FormControl>
          </Stack>
        )}
        {/*
          Interim note, shown in editable AND readonly mode (ProjDetails
          displays the same values, which would otherwise look applied).
          Covers the four USER inputs only -- the Regional Multiplier is the
          system's value and IS applied today. Delete when
          decarb4-calibration-check ships (its 003-implement-clamp lists this).
        */}
        <Typography
          role='note'
          sx={{
            px: 1.5,
            py: 0.75,
            fontSize: '0.75rem',
            fontStyle: 'italic',
            color: 'rgba(14, 70, 63, 0.8)',
            bgcolor: '#D5E4D5',
            border: '1px solid',
            borderTop: 'none',
            borderColor: 'rgba(14, 70, 63, 0.2)',
          }}
        >
          {CALIBRATION_NOT_YET_APPLIED_NOTE}
        </Typography>
      </Box>

      {/* Cost Summary */}
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'rgba(14, 70, 63, 0.2)',
          borderRadius: '4px 4px 0 0',
          overflow: 'hidden',
        }}
      >
        {/* Full-width Cost Summary header */}
        <Box sx={{ px: 1.5, py: 0.75, bgcolor: '#0a2a26' }}>
          <Typography
            variant='caption'
            sx={{
              fontFamily: FONT_HEADER,
              fontWeight: 700,
              textTransform: 'uppercase',
              color: 'white',
            }}
          >
            Cost Summary
          </Typography>
        </Box>

        {/* Content */}
        <Box sx={{ bgcolor: '#D5E4D5' }}>
          {/* Cost Estimate Validation sub-section (read-only mirrors) */}
          <SubSectionHeader>Cost Estimate Validation</SubSectionHeader>
          <SummaryRow label='Owner Expense' value='—' bgcolor='#D5E4D5' />
          <SummaryRow label='Land Values' value='—' bgcolor='#CCDCCC' />
          <SummaryRow
            label='Construction Cost Escalation'
            value='—'
            bgcolor='#D5E4D5'
          />

          {/* Budget Request sub-section */}
          <SubSectionHeader>Budget Request</SubSectionHeader>

          {/* Capital — FY group */}
          <Box
            sx={{
              px: 1.5,
              py: 0.5,
              bgcolor: '#27786f',
              borderBottom: '1px solid',
              borderColor: 'rgba(14, 70, 63, 0.2)',
            }}
          >
            <Typography
              variant='caption'
              sx={{
                fontWeight: 600,
                color: '#a0ccd6',
                textTransform: 'uppercase',
              }}
            >
              Capital — {fyName}
            </Typography>
          </Box>
          <SummaryRow
            label='Owner'
            value={formatTotal(capitalFyOwner)}
            bgcolor='#D5E4D5'
            pl={8}
          />
          <SummaryRow
            label='Contractor'
            value={formatTotal(capitalFyContractor)}
            bgcolor='#CCDCCC'
            pl={8}
          />
          <SummaryRow
            label='Total'
            value={formatTotal(capitalFyTotal)}
            bgcolor='#D5E4D5'
            pl={8}
          />

          {/* Capital — Total group */}
          <Box
            sx={{
              px: 1.5,
              py: 0.5,
              bgcolor: '#27786f',
              borderBottom: '1px solid',
              borderColor: 'rgba(14, 70, 63, 0.2)',
            }}
          >
            <Typography
              variant='caption'
              sx={{
                fontWeight: 600,
                color: '#a0ccd6',
                textTransform: 'uppercase',
              }}
            >
              Capital — Total Project
            </Typography>
          </Box>
          <SummaryRow
            label='Owner'
            value={formatTotal(capitalTotalOwner)}
            bgcolor='#D5E4D5'
            pl={8}
          />
          <SummaryRow
            label='Contractor'
            value={formatTotal(capitalTotalContractor)}
            bgcolor='#CCDCCC'
            pl={8}
          />
          <SummaryRow
            label='Total'
            value={formatTotal(capitalTotalTotal)}
            bgcolor='#D5E4D5'
            pl={8}
          />

          {/* Operation group */}
          <Box
            sx={{
              px: 1.5,
              py: 0.5,
              bgcolor: '#27786f',
              borderBottom: '1px solid',
              borderColor: 'rgba(14, 70, 63, 0.2)',
            }}
          >
            <Typography
              variant='caption'
              sx={{
                fontWeight: 600,
                color: '#a0ccd6',
                textTransform: 'uppercase',
              }}
            >
              Operation
            </Typography>
          </Box>
          <SummaryRow
            label='Owner'
            value={formatTotal(operationOwner)}
            bgcolor='#D5E4D5'
            pl={8}
          />
          <SummaryRow
            label='Contractor'
            value={formatTotal(operationContractor)}
            bgcolor='#CCDCCC'
            pl={8}
          />
          <SummaryRow
            label='Total'
            value={formatTotal(operationTotal)}
            bgcolor='#D5E4D5'
            pl={8}
          />

          {/* Grand Total */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              px: 1.5,
              py: 0.75,
              bgcolor: 'background.paper',
            }}
          >
            <Typography
              variant='caption'
              sx={{
                fontFamily: FONT_HEADER,
                fontWeight: 700,
                textTransform: 'uppercase',
                color: 'text.primary',
              }}
            >
              Grand Total
            </Typography>
            <Typography
              variant='caption'
              sx={{ fontWeight: 700, color: 'text.primary' }}
            >
              {formatTotal(grandTotal)}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Stack>
  );
}
